import { PLAYER } from '@/config/player';
import type { SaveData } from '@/core/save';
import { CARDS, type CardDef } from '@/upgrades/cards';
import type { RunState } from './run-state';

/** Mixes the saved room into a resumed run's seed so the resumed run varies */
const RESUME_SEED_MIX = 0x9e3779b9;
/** Old saves stored hull CELLS (≤ this) instead of HP */
const LEGACY_CELL_MAX = 10;

/** Ship fields the save carries (the rest is re-derived from the cards). */
export interface ShipSave {
  equippedIds: ReadonlyArray<string>;
  hp: number;
  armor: number;
  bulletsFired: number;
}

/** Ship values restored from a save — applied after the card rebuild. */
export interface ShipRestore {
  equippedIds: string[];
  hp: number;
  armor: number;
  bulletsFired: number;
  /** Death checkpoint (X11): resume IN the failed room, not at the shop */
  inFight: boolean;
}

/**
 * RunState → SaveData (autosave at every shop break, after each purchase
 * and at the death checkpoint — docs/01). Key order is the save format.
 * @param shopOpen the shop break is showing (the death bay is NOT a shop
 *   save: Continue re-enters the failed room at its wave — X11)
 */
export function buildSave(run: RunState, ship: ShipSave, shopOpen: boolean): SaveData {
  return {
    mode: run.mode,
    ...(run.mode === 'sector' ? { sector: run.sectorIndex } : {}),
    seed: run.seed,
    roomNumber: run.roomNumber,
    arenaIndex: run.arenaIndex,
    ...run.rotation.snapshot(),
    resumeAt: shopOpen && !run.deathShop ? 'shop' : 'fight',
    ...(run.currentFight ? { currentFight: { ...run.currentFight } } : {}),
    // The death bay's preview pick is just the retry — never stored
    ...(run.nextPick && !run.deathShop ? { nextPick: { ...run.nextPick } } : {}),
    gems: run.gems,
    red: run.red,
    ownedCardIds: run.ownedCards.map((c) => c.id),
    inventory: [...run.inventory],
    ...(run.pendingWeapon ? { pendingWeapon: run.pendingWeapon } : {}),
    equippedIds: [...ship.equippedIds],
    hp: ship.hp,
    armor: ship.armor,
    storedArmor: run.storedArmor,
    totalRuns: run.totalRuns,
    waveIndex: run.wave.index,
    ...(run.earned.length > 0 ? { earned: [...run.earned] } : {}),
    stats: { ...run.stats, bulletsFired: ship.bulletsFired },
  };
}

/**
 * SaveData → RunState (Continue). Old saves are migrated field by field.
 * @param fallbackBossId fight to replay when a pre-rotation save has none
 */
export function restoreRun(run: RunState, save: SaveData, fallbackBossId: string): ShipRestore {
  // Pre-sector saves carry no mode: they continue as endless runs
  run.setMode(save.mode ?? 'endless', save.sector ?? 0);
  run.clearTransient();
  run.totalRuns = save.totalRuns ?? 1;
  run.reseed(((save.seed ^ Math.imul(save.roomNumber, RESUME_SEED_MIX)) >>> 0) || 1);
  run.roomNumber = save.roomNumber;
  run.rotation.restore(save); // old saves: unseen bosses come first
  run.gems = save.gems;
  run.red = save.red;
  run.inventory = [...save.inventory];
  run.pendingWeapon = save.pendingWeapon ?? null;
  run.ownedCards = save.ownedCardIds
    .map((id) => CARDS.find((c) => c.id === id))
    .filter((c): c is CardDef => c !== undefined);
  run.stats = { ...save.stats };
  run.earned = [...(save.earned ?? [])];
  run.storedArmor = Math.min(PLAYER.maxArmor, save.storedArmor ?? 0);
  run.nextPick = save.nextPick ?? null;
  // X11: a death-checkpoint save drops straight back into the FAILED room at
  // its wave; a shop-break save reopens the shop over the cleared arena
  run.currentFight = save.currentFight ??
    { id: save.recentBossIds?.at(-1) ?? fallbackBossId, level: 1 };
  const inFight = save.resumeAt === 'fight' && save.currentFight !== undefined;
  run.wave.index = inFight ? save.waveIndex ?? 0 : Number.MAX_SAFE_INTEGER;
  return {
    equippedIds: save.equippedIds ??
      [save.primaryId ?? run.inventory[0]!, ...(save.secondaryId ? [save.secondaryId] : [])],
    hp: save.hp <= LEGACY_CELL_MAX ? save.hp * PLAYER.cellHp : save.hp,
    armor: Math.min(PLAYER.maxArmor, save.armor ?? 0),
    bulletsFired: save.stats.bulletsFired,
    inFight,
  };
}
